/**
 * modificaListino — cosa cambia quando la console modifica uno strumento a canone (atlante cap. 251, tappa 4).
 *
 * Due tipi di cambio, con conseguenze diverse:
 *  - il PREZZO (canone, quota compresa, prezzo dell'extra): su Stripe un prezzo non si modifica, se ne crea uno
 *    nuovo. Chi si abbona da quel momento paga il nuovo; chi è già abbonato resta al prezzo di prima, che il listino
 *    ricorda in `prezzi_precedenti` (`strumentoPerLAzienda`). Il canone e il consumo sono due prezzi separati: cambia
 *    solo quello toccato.
 *  - il resto (in vendita, cosa fa al tetto): si scrive e basta.
 *
 * L'unità di consumo NON si cambia da qui: è legata al misuratore di Stripe e al punto del codice che conta. Per lo
 * stesso motivo uno strumento SOLO a canone (`consumo: null`) resta senza consumo: quota ed extra non si accettano.
 *
 * Nucleo PURO, condiviso fra la finestra della console (per dire subito cosa non va) e la callable
 * `consoleModificaStrumento` (che decide davvero).
 */

import { COMPORTAMENTI_AL_TETTO, type ComportamentoAlTetto, type ConsumoDelloStrumento, type StrumentoACanone } from './listino';
import { validaTetto } from './tetto';

export const CANONE_MASSIMO_EUR = 10_000;
export const QUOTA_MASSIMA = 1_000_000;
export const EXTRA_MASSIMO_EUR = 100;

export interface RichiestaModificaStrumento {
  readonly strumento: string;
  readonly canone_mensile_eur?: number;
  readonly quota_compresa?: number;
  readonly prezzo_extra_eur?: number;
  readonly in_vendita?: boolean;
  readonly al_tetto?: ComportamentoAlTetto;
  /** Il tetto di spesa di partenza, in euro; null = il canone (`StrumentoACanone.tetto_predefinito_eur`). */
  readonly tetto_predefinito_eur?: number | null;
}

export type ErroreModifica =
  | 'canone_non_valido'
  | 'quota_non_valida'
  | 'extra_non_valido'
  | 'al_tetto_non_valido'
  | 'tetto_predefinito_non_valido'
  /** Quota o extra chiesti per uno strumento solo a canone: non ha un consumo da contare. */
  | 'senza_consumo'
  | 'niente_da_cambiare';

export interface PianoDellaModifica {
  readonly canone_mensile_eur: number;
  /** Null per uno strumento solo a canone (resta così). */
  readonly consumo: ConsumoDelloStrumento | null;
  readonly in_vendita: boolean;
  readonly al_tetto: ComportamentoAlTetto;
  readonly tetto_predefinito_eur: number | null;
  /** Serve un prezzo Stripe nuovo per il canone. */
  readonly nuovo_canone: boolean;
  /** Serve un prezzo Stripe nuovo per il consumo (quota o prezzo dell'extra cambiati). */
  readonly nuovo_consumo: boolean;
}

/** Al massimo quattro decimali di euro (0,155 € = 15,5 centesimi): Stripe li accetta, di più non hanno senso. */
function quattroDecimali(v: number): boolean {
  return Math.abs(Math.round(v * 10_000) - v * 10_000) < 1e-6;
}

function due(v: number): boolean {
  return Math.abs(Math.round(v * 100) - v * 100) < 1e-6;
}

/** I tre numeri di un prezzo sono validi? Le stesse regole per il listino e per le condizioni su misura. */
export function erroreDelleCondizioni(canone: unknown, quota: unknown, extra: unknown): 'canone_non_valido' | 'quota_non_valida' | 'extra_non_valido' | null {
  if (typeof canone !== 'number' || !Number.isFinite(canone) || canone < 0 || canone > CANONE_MASSIMO_EUR || !due(canone)) return 'canone_non_valido';
  if (typeof quota !== 'number' || !Number.isInteger(quota) || quota < 0 || quota > QUOTA_MASSIMA) return 'quota_non_valida';
  if (typeof extra !== 'number' || !Number.isFinite(extra) || extra < 0 || extra > EXTRA_MASSIMO_EUR || !quattroDecimali(extra)) return 'extra_non_valido';
  return null;
}

export function pianoDellaModifica(
  attuale: StrumentoACanone,
  r: RichiestaModificaStrumento,
): { readonly ok: true; readonly piano: PianoDellaModifica } | { readonly ok: false; readonly errore: ErroreModifica } {
  const canone = r.canone_mensile_eur ?? attuale.canone_mensile_eur;
  const consumoAttuale = attuale.consumo;
  if (consumoAttuale === null && (r.quota_compresa !== undefined || r.prezzo_extra_eur !== undefined)) return { ok: false, errore: 'senza_consumo' };
  const quota = r.quota_compresa ?? consumoAttuale?.quota_compresa ?? 0;
  const extra = r.prezzo_extra_eur ?? consumoAttuale?.prezzo_extra_eur ?? 0;
  const alTetto = r.al_tetto ?? attuale.al_tetto;
  const erroreNumeri = erroreDelleCondizioni(canone, quota, extra);
  if (erroreNumeri) return { ok: false, errore: erroreNumeri };
  // Solo canone: il canone resta un canone vero (uno strumento senza consumo e a 0 € non si vende).
  if (consumoAttuale === null && canone <= 0) return { ok: false, errore: 'canone_non_valido' };
  if (!COMPORTAMENTI_AL_TETTO.includes(alTetto)) return { ok: false, errore: 'al_tetto_non_valido' };
  const tetto = r.tetto_predefinito_eur === undefined ? { ok: true as const, eur: attuale.tetto_predefinito_eur } : validaTetto(r.tetto_predefinito_eur);
  if (!tetto.ok) return { ok: false, errore: 'tetto_predefinito_non_valido' };
  const piano: PianoDellaModifica = {
    canone_mensile_eur: canone,
    consumo: consumoAttuale === null ? null : { unita: consumoAttuale.unita, quota_compresa: quota, prezzo_extra_eur: extra },
    in_vendita: r.in_vendita ?? attuale.in_vendita,
    al_tetto: alTetto,
    tetto_predefinito_eur: tetto.eur,
    nuovo_canone: canone !== attuale.canone_mensile_eur,
    nuovo_consumo: consumoAttuale !== null && (quota !== consumoAttuale.quota_compresa || extra !== consumoAttuale.prezzo_extra_eur),
  };
  const cambiaAltro = piano.in_vendita !== attuale.in_vendita || piano.al_tetto !== attuale.al_tetto
    || piano.tetto_predefinito_eur !== attuale.tetto_predefinito_eur;
  if (!piano.nuovo_canone && !piano.nuovo_consumo && !cambiaAltro) return { ok: false, errore: 'niente_da_cambiare' };
  return { ok: true, piano };
}

/** Un prezzo in euro come centesimi con i decimali, nella forma che vuole Stripe (0,155 € = «15.5»). */
function centesimiConDecimali(eur: number): string {
  return String(Math.round(eur * 100 * 10_000) / 10_000);
}

/**
 * La forma del prezzo a consumo su Stripe. Con una quota compresa: due scaglioni graduati, la quota a zero e poi
 * l'extra. Senza quota (i servizi che si pagano solo a consumo, `soloAConsumo`): un prezzo per unità, perché Stripe non
 * accetta uno scaglione che finisce a 0. È la stessa forma per lo script di preparazione
 * (`scripts/strumenti/setup-stripe-strumenti.cjs`), la console e i prezzi su misura.
 */
export type FormaDelPrezzoDiConsumo =
  | {
    readonly billing_scheme: 'tiered';
    readonly tiers_mode: 'graduated';
    readonly tiers: ReadonlyArray<{ readonly up_to: number | 'inf'; readonly unit_amount_decimal: string }>;
  }
  | { readonly billing_scheme: 'per_unit'; readonly unit_amount_decimal: string };

export function formaDelPrezzoDiConsumo(consumo: ConsumoDelloStrumento): FormaDelPrezzoDiConsumo {
  const extra = centesimiConDecimali(consumo.prezzo_extra_eur);
  if (consumo.quota_compresa === 0) return { billing_scheme: 'per_unit', unit_amount_decimal: extra };
  return {
    billing_scheme: 'tiered',
    tiers_mode: 'graduated',
    tiers: [
      { up_to: consumo.quota_compresa, unit_amount_decimal: '0' },
      { up_to: 'inf', unit_amount_decimal: extra },
    ],
  };
}
